import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { ClientEvent, SyncState } from '../constants/matrixEvents';
import matrixService from '../services/matrixService';
import networkService from '../services/networkService';
import logger from '../utils/logger';

const HIDE_DELAY_MS = 5000;

// Two-stage debounce before a Matrix sync ERROR is treated as "connection lost".
// matrix-js-sdk only reaches SyncState.Error after repeated failures, then backs
// its recovery keepalive off by 5-10s (sync.js FAILED_SYNC_ERROR_THRESHOLD + the
// pokeKeepAlive 5000+rand(5000) reschedule). So a transient drop — an OS
// sleep/tab-freeze killing the in-flight /sync (net::ERR_NETWORK_IO_SUSPENDED),
// or a logout teardown aborting it — leaves the client stuck in ERROR for several
// seconds even though connectivity is fine. A single timer can't tell that apart
// (and on sleep it is frozen and fires on wake while still ERROR), so we:
//   1. wait OFFLINE_GRACE_MS to absorb quick recoveries, then
//   2. actively probe (retrySyncImmediately bypasses the SDK backoff) and
//      re-confirm over OFFLINE_CONFIRM_MS — a window armed WHEN the grace fires,
//      so on wake it runs post-wake and observes the SDK's near-instant recovery.
// Only a still-ERROR-after-confirm (genuine outage) surfaces the banner. (NED-4045)
const OFFLINE_GRACE_MS = 3000;
const OFFLINE_CONFIRM_MS = 2000;

// Cadence for detecting a Matrix client swap so the sync listener can re-bind
// to a new client after logout→re-login (NED-4045 stale-listener fix).
const CLIENT_POLL_MS = 500;

const isPreparedState = (s) => (
  s === (SyncState && SyncState.Prepared) ||
  s === (SyncState && SyncState.Syncing) ||
  s === 'PREPARED' ||
  s === 'SYNCING'
);

const isErrorState = (s) => s === (SyncState && SyncState.Error) || s === 'ERROR';

/**
 * Encapsulates network-status state and the Matrix sync-retry action.
 *
 * Keeps service imports out of presentational components by exposing:
 *  - `status`               – 'hidden' | 'offline' | 'online'
 *  - `isReconnectPending`   – true while a retrySyncImmediately call is in-flight
 *  - `hasClient`            – true when an authenticated Matrix client is available
 *  - `retrySyncImmediately` – triggers an immediate Matrix sync retry
 *
 * @param {boolean} isAuthenticated – when false the banner is force-hidden and
 *   no sync listener is attached (so it never shows on the login page / during
 *   logout). Defaults to true so the hook can be exercised standalone in tests.
 */
const useNetworkStatus = (isAuthenticated = true) => {
  const getInitialStatus = () => {
    if (!isAuthenticated) return 'hidden';
    // Only a definitive browser-offline signal shows the banner at mount; a
    // non-prepared sync state must not flash (it is deferred via the grace
    // window by the sync listener below). NED-4045.
    return networkService.getIsOnline() ? 'hidden' : 'offline';
  };

  const [status, setStatus] = useState(getInitialStatus);
  const [isReconnectPending, setIsReconnectPending] = useState(false);
  // Bumped on session invalidation / client swap so `hasClient` re-evaluates.
  const [, bumpRender] = useReducer((n) => n + 1, 0);
  const hideTimerRef = useRef(null);
  const offlineGraceTimerRef = useRef(null);
  // Seeded gated on auth: an offline+unauthenticated cold start must not leave a
  // stale "offline was shown" flag that later flashes a spurious "Connected"
  // banner after login (NED-4045). Re-seeded on the login transition below.
  const hasShownOfflineRef = useRef(isAuthenticated ? !networkService.getIsOnline() : false);

  // Latest auth flag, read inside timer callbacks without a stale closure.
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;
  // Previous auth flag, to distinguish a login transition from a steady state.
  const prevAuthenticatedRef = useRef(isAuthenticated);

  // Read on every render so login/logout transitions are reflected without
  // depending on a network-event arrival. Gated on auth so the "Reconnect now"
  // button can never appear once logged out.
  const hasClient = isAuthenticated && Boolean(matrixService.getClient?.());

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const clearOfflineGrace = useCallback(() => {
    if (offlineGraceTimerRef.current) {
      clearTimeout(offlineGraceTimerRef.current);
      offlineGraceTimerRef.current = null;
    }
  }, []);

  const showOffline = useCallback(() => {
    if (!isAuthenticatedRef.current) return;
    clearHideTimer();
    clearOfflineGrace();
    hasShownOfflineRef.current = true;
    setIsReconnectPending(false);
    setStatus('offline');
  }, [clearHideTimer, clearOfflineGrace]);

  const showOnlineConfirmation = useCallback(() => {
    clearOfflineGrace();
    if (!isAuthenticatedRef.current) return;
    if (!hasShownOfflineRef.current) return;

    clearHideTimer();
    setIsReconnectPending(false);
    setStatus('online');
    hideTimerRef.current = setTimeout(() => {
      setStatus('hidden');
      hideTimerRef.current = null;
      hasShownOfflineRef.current = false;
    }, HIDE_DELAY_MS);
  }, [clearHideTimer, clearOfflineGrace]);

  // Defer showing offline on a sync ERROR, then ACTIVELY confirm rather than
  // trusting a possibly-stale ERROR state (see OFFLINE_GRACE_MS comment). The
  // confirm timer is armed WHEN the grace timer fires, so on wake-from-sleep it
  // begins counting post-wake and observes the SDK's recovery instead of a
  // frozen grace timer firing into a momentarily-stale ERROR. (NED-4045)
  const scheduleOffline = useCallback(() => {
    if (offlineGraceTimerRef.current) return; // a phase is already pending

    const stillOffline = () => {
      if (!isAuthenticatedRef.current) return false;
      const client = matrixService.getClient?.();
      if (!client || typeof client.getSyncState !== 'function') return false;
      if (!isErrorState(client.getSyncState())) return false;
      return networkService.getIsOnline();
    };

    // Phase 1: grace debounce — absorbs quick recoveries.
    offlineGraceTimerRef.current = setTimeout(() => {
      offlineGraceTimerRef.current = null;
      if (!stillOffline()) return;

      // Phase 2: probe now (bypassing the SDK's 5-10s keepalive backoff) and
      // re-confirm over a fresh real-time window before committing to offline.
      try {
        const probe = matrixService.retrySyncImmediately?.();
        if (probe && typeof probe.catch === 'function') probe.catch(() => { /* non-fatal */ });
      } catch { /* non-fatal */ }

      offlineGraceTimerRef.current = setTimeout(() => {
        offlineGraceTimerRef.current = null;
        if (stillOffline()) showOffline();
      }, OFFLINE_CONFIRM_MS);
    }, OFFLINE_GRACE_MS);
  }, [showOffline]);

  useEffect(() => {
    const evaluateOnline = ({ isOnline }) => {
      if (!isOnline) {
        // A browser `offline` event is authoritative — show immediately.
        showOffline();
        return;
      }

      // Browser believes we're online — consult Matrix sync if client exists
      const client = matrixService.getClient?.();
      if (!client || typeof client.getSyncState !== 'function') {
        showOnlineConfirmation();
        return;
      }

      if (isPreparedState(client.getSyncState())) {
        showOnlineConfirmation();
      } else {
        // Matrix sync not yet prepared — defer through the grace window rather
        // than flashing offline; sync usually catches up within seconds.
        scheduleOffline();
      }
    };

    const unsubscribe = networkService.subscribe(evaluateOnline);

    return () => {
      clearHideTimer();
      clearOfflineGrace();
      unsubscribe();
    };
  }, [clearHideTimer, clearOfflineGrace, showOffline, showOnlineConfirmation, scheduleOffline]);

  // Listen to Matrix client sync transitions. Re-binds whenever the client
  // instance changes so network status keeps working after a logout→re-login
  // client swap (NED-4045 stale-listener fix). Skipped entirely while logged out.
  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let attachedClient = null;
    let pollTimer = null;

    const syncListener = (state) => {
      if (isPreparedState(state)) {
        // Recovery cancels any pending offline and confirms reconnection.
        clearOfflineGrace();
        if (networkService.getIsOnline()) showOnlineConfirmation();
      } else if (isErrorState(state)) {
        // STOPPED is deliberately excluded: it signals an intentional
        // client.stopClient() (e.g. logout), not a network failure. ERROR is
        // deferred through the grace window rather than shown immediately, so
        // suspend/wake and teardown blips don't flash the banner. (NED-4045)
        scheduleOffline();
      }
    };

    const detach = (client) => {
      if (client && typeof client.off === 'function') {
        try { client.off(ClientEvent.Sync, syncListener); } catch { /* ignore */ }
      }
    };

    const attachTo = (client) => {
      attachedClient = client;
      client.on(ClientEvent.Sync, syncListener);
      bumpRender(); // refresh hasClient now that a client is present
      if (typeof client.getSyncState === 'function') {
        syncListener(client.getSyncState());
      }
    };

    // Re-bind when the current client differs from the one we're attached to.
    const syncBinding = () => {
      const client = matrixService.getClient?.();
      if (client === attachedClient) return;
      if (attachedClient) detach(attachedClient);
      if (client && typeof client.on === 'function') {
        attachTo(client);
      } else {
        attachedClient = null;
        bumpRender();
      }
    };

    syncBinding();
    pollTimer = setInterval(syncBinding, CLIENT_POLL_MS);

    return () => {
      if (pollTimer) clearInterval(pollTimer);
      detach(attachedClient);
      attachedClient = null;
    };
  }, [isAuthenticated, clearOfflineGrace, showOnlineConfirmation, scheduleOffline]);

  // React to auth transitions (NED-4045):
  //  - logout (→false): force-hide and tear down timers immediately, so the
  //    banner can never linger on the way to /login. This covers a clean
  //    user-initiated logout, which does not dispatch matrix:session-invalidated.
  //  - login (false→true): start the session clean, re-seeding hasShownOffline
  //    from current connectivity so a stale flag from an offline/unauthenticated
  //    boot can't flash a spurious "Connected" banner. A steady authenticated
  //    mount is left untouched, preserving the normal offline→online confirmation.
  useEffect(() => {
    const wasAuthenticated = prevAuthenticatedRef.current;
    prevAuthenticatedRef.current = isAuthenticated;

    if (!isAuthenticated) {
      clearHideTimer();
      clearOfflineGrace();
      hasShownOfflineRef.current = false;
      setIsReconnectPending(false);
      setStatus('hidden');
      bumpRender();
    } else if (!wasAuthenticated) {
      // Fresh session: nothing has been shown yet, so no pending "Connected"
      // confirmation is owed. (Login requires connectivity, so we're online
      // here; a later sustained offline re-sets this via the grace path.)
      clearHideTimer();
      clearOfflineGrace();
      hasShownOfflineRef.current = false;
      setIsReconnectPending(false);
      bumpRender();
    }
  }, [isAuthenticated, clearHideTimer, clearOfflineGrace]);

  useEffect(() => {
    const handleSessionInvalidated = () => {
      // The session is gone — there is nothing to "reconnect" to. Clear any
      // offline banner the sync-teardown may have triggered, so the user
      // does not see a misleading "Connection lost" flash on the way to /login.
      clearHideTimer();
      clearOfflineGrace();
      hasShownOfflineRef.current = false;
      setIsReconnectPending(false);
      setStatus('hidden');
      bumpRender();
    };
    window.addEventListener('matrix:session-invalidated', handleSessionInvalidated);
    return () => {
      window.removeEventListener('matrix:session-invalidated', handleSessionInvalidated);
    };
  }, [clearHideTimer, clearOfflineGrace]);

  const retrySyncImmediately = useCallback(async () => {
    if (isReconnectPending) return;
    if (!isAuthenticatedRef.current) return;
    if (!matrixService.getClient?.()) return;

    setIsReconnectPending(true);

    try {
      await matrixService.retrySyncImmediately();
    } catch (error) {
      logger.warn('useNetworkStatus: reconnect attempt failed', {
        errorMessage: error?.message,
        errorName: error?.name,
      });
    } finally {
      setIsReconnectPending(false);
    }
  }, [isReconnectPending]);

  return { status, isReconnectPending, hasClient, retrySyncImmediately };
};

export default useNetworkStatus;
